<script setup>
import { ref, onMounted } from 'vue';
import { setMeta } from '@/utils/util';

onMounted(() => {
  document.title = "Code Beautifier - Format and Beautify Your Code";
  setMeta("description", "Free online code beautifier and formatter. Support JavaScript, HTML, CSS, and JSON with consistent formatting rules. Make your code clean and readable.");
  setMeta("keywords", "code beautifier,code formatter,javascript formatter,html formatter,css formatter,json formatter,code formatting tool");

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Code Beautifier",
    "description": "Online code beautifier and formatter with support for multiple languages",
    "applicationCategory": "DeveloperApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(structuredData);
  document.head.appendChild(script);
});
import { ElMessage } from 'element-plus';
import beautify from 'js-beautify';
import { js as beautifyJS, css as beautifyCSS, html as beautifyHTML } from 'js-beautify';

const code = ref('');
const formattedCode = ref('');
const language = ref('javascript');
const error = ref('');
const loading = ref(false);

const languageOptions = [
  { label: 'JavaScript', value: 'javascript' },
  { label: 'HTML', value: 'html' },
  { label: 'CSS', value: 'css' },
  { label: 'JSON', value: 'json' },
];

onMounted(() => {
  document.title = "Code Beautifier - Online Code Formatting Tool";
  setMeta("description", "Free online code beautifier tool that supports multiple programming languages including JavaScript, HTML, CSS, and JSON. Provides code formatting, beautification, and standardization features.")
  setMeta("keywords", "code formatter,code beautifier,code standardizer,online formatting tool,JavaScript formatter,HTML formatter,CSS formatter")
});

const formatJavaScript = (input) => {
  try {
    // Validate JavaScript syntax using Function constructor
    new Function(input);
    return beautifyJS(input, {
      indent_size: 2,
      space_in_empty_paren: true,
      preserve_newlines: true,
      max_preserve_newlines: 2,
      brace_style: 'collapse',
      break_chained_methods: true,
      keep_array_indentation: true,
      space_before_conditional: true,
      unescape_strings: true,
      wrap_line_length: 80,
      end_with_newline: true,
      comma_first: false,
      operator_position: 'before-newline'
    });
  } catch (e) {
    throw new Error('JavaScript syntax error');
  }
};

const formatHTML = (input) => {
  try {
    return beautifyHTML(input, {
      indent_size: 2,
      wrap_line_length: 80,
      preserve_newlines: true,
      max_preserve_newlines: 2,
      unformatted: ['code', 'pre', 'em', 'strong', 'span'],
    });
  } catch (e) {
    throw new Error('HTML formatting failed');
  }
};

const formatCSS = (input) => {
  try {
    return beautifyCSS(input, {
      indent_size: 2,
      preserve_newlines: true,
      max_preserve_newlines: 2,
    });
  } catch (e) {
    throw new Error('CSS formatting failed');
  }
};

const formatJSON = (input) => {
  try {
    const parsed = JSON.parse(input);
    return JSON.stringify(parsed, null, 2);
  } catch (e) {
    throw new Error('Invalid JSON format');
  }
};

const formatCode = async () => {
  if (!code.value.trim()) {
    error.value = 'Please enter code to format';
    return;
  }

  error.value = '';
  loading.value = true;

  try {
    switch (language.value) {
      case 'javascript':
        formattedCode.value = formatJavaScript(code.value);
        break;
      case 'html':
        formattedCode.value = formatHTML(code.value);
        break;
      case 'css':
        formattedCode.value = formatCSS(code.value);
        break;
      case 'json':
        formattedCode.value = formatJSON(code.value);
        break;
    }
  } catch (err) {
    console.error('Formatting error:', err);
    error.value = `Formatting error: ${err.message}`;
    formattedCode.value = '';
  } finally {
    loading.value = false;
  }
};

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(formattedCode.value);
    ElMessage.success('Code copied to clipboard!');
  } catch (err) {
    ElMessage.error('Failed to copy code to clipboard');
  }
};

const clearCode = () => {
  code.value = '';
  formattedCode.value = '';
  error.value = '';
};

const loadExample = () => {
  switch (language.value) {
    case 'javascript':
      code.value = `function example(){const x=1;const y=2;return x+y;}const obj={a:1,b:2,c:3};const arr=[1,2,3,4,5];`;
      break;
    case 'html':
      code.value = `<!DOCTYPE html><html><head><title>Example</title></head><body><div class="container"><h1>Hello World</h1><p>This is an example.</p></div></body></html>`;
      break;
    case 'css':
      code.value = `.container{width:100%;max-width:1200px;margin:0 auto;padding:20px;}.button{display:inline-block;padding:10px 20px;border-radius:4px;background-color:#007bff;color:white;}`;
      break;
    case 'json':
      code.value = `{"name":"John","age":30,"city":"New York","hobbies":["reading","music","sports"],"address":{"street":"123 Main St","zip":"10001"}}`;
      break;
  }
  formatCode();
};
</script>

<template>
  <div class="tool-container">
    <h1 style="margin-bottom: 20px">Code Beautifier</h1>
    
    <div class="controls">
      <el-select v-model="language" placeholder="Select Language" @change="clearCode">
        <el-option
          v-for="option in languageOptions"
          :key="option.value"
          :label="option.label"
          :value="option.value"
        />
      </el-select>
      
      <div class="buttons">
        <el-button type="primary" @click="formatCode" :loading="loading">Format Code</el-button>
        <el-button @click="clearCode">Clear</el-button>
        <el-button @click="loadExample">Load Example</el-button>
      </div>
    </div>

    <div class="code-area">
      <div class="input-section">
        <label>Input Code:</label>
        <el-input
          v-model="code"
          type="textarea"
          :rows="15"
          placeholder="Paste your code here..."
          :spellcheck="false"
        />
      </div>
      
      <div class="output-section">
        <div class="output-header">
          <label>Formatted Code:</label>
          <el-button 
            v-if="formattedCode"
            type="primary" 
            link 
            @click="copyToClipboard"
          >
            Copy to Clipboard
          </el-button>
        </div>
        <el-input
          v-model="formattedCode"
          type="textarea"
          :rows="15"
          readonly
          :spellcheck="false"
        />
      </div>
    </div>

    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      style="margin-top: 20px;"
    />

    <div class="introduce">
      <h2>What is Code Beautifier?💻</h2>
      <p>
        Code Beautifier is a powerful online tool that helps you format and beautify your code.
        It supports multiple programming languages and provides consistent formatting rules.
      </p>
      
      <h3>Features</h3>
      <ul>
        <li>Support for multiple programming languages</li>
        <li>Consistent code formatting</li>
        <li>Real-time formatting</li>
        <li>Easy copy to clipboard</li>
        <li>Customizable formatting options</li>
      </ul>

      <h3>Supported Languages</h3>
      <ul>
        <li>JavaScript - Format JS code with modern standards</li>
        <li>HTML - Properly indent and organize HTML markup</li>
        <li>CSS - Format stylesheets for better readability</li>
        <li>JSON - Format and validate JSON data</li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tool-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.controls {
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.buttons {
  display: flex;
  gap: 10px;
}

.code-area {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
}

.input-section,
.output-section {
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-weight: bold;
    color: #606266;
  }
}

.output-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

:deep(.el-textarea__inner) {
  font-family: monospace;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre;
  overflow-wrap: normal;
  overflow-x: auto;
}

.introduce {
  margin-top: 40px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;

  h2 {
    margin-bottom: 16px;
  }

  h3 {
    margin: 20px 0 10px;
    color: #2c3e50;
  }

  p {
    margin-bottom: 16px;
    line-height: 1.6;
    color: #606266;
  }

  ul {
    margin: 0;
    padding-left: 20px;
    
    li {
      margin-bottom: 8px;
      color: #606266;
    }
  }
}

@media (max-width: 768px) {
  .code-area {
    grid-template-columns: 1fr;
  }
}
</style>